import { useState } from 'react'
import { CARDS } from '@lunary/tarot-data'
import type { TarotCard } from '@lunary/tarot-data'
import { Modal } from '@lunary/ui-web/modal'
import { pickFaceFor, useDeck } from '@lunary/ui-web/deck'
import { toRoman } from '@lunary/app-core/roman'
import { CardFace } from '../../shared/ui/card-face'
import { useThreeCardTexts } from '../decks/queries'
import styles from '../daily-card/daily-card.module.css'

type Filter = 'all' | 'major' | NonNullable<TarotCard['suit']>

const FILTERS: readonly { key: Filter; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'major', label: 'Старшие' },
  { key: 'wands', label: 'Жезлы' },
  { key: 'cups', label: 'Кубки' },
  { key: 'swords', label: 'Мечи' },
  { key: 'pentacles', label: 'Пентакли' },
]

type Props = {
  open: boolean
  position: number
  current: string
  taken: readonly string[]
  pending: boolean
  error: string | null
  onClose: () => void
  onPick: (cardId: string) => void
}

export function ThreeCardPicker({ open, position, current, taken, pending, error, onClose, onPick }: Props) {
  const deck = useDeck()
  const texts = useThreeCardTexts()
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const [chosen, setChosen] = useState<string>(current)
  const q = query.trim().toLowerCase()
  const shown = CARDS.filter(
    (c) => (filter === 'all' || (filter === 'major' ? c.arcana === 'major' : c.suit === filter)) && (!q || c.name.toLowerCase().includes(q)),
  )
  const faceOf = (c: TarotCard) => pickFaceFor(deck, c)
  const available = (c: TarotCard) => c.id === current || (!taken.includes(c.id) && (c.arcana === 'major' || faceOf(c) !== null))
  const preview = CARDS.find((c) => c.id === chosen) ?? null
  const text = preview ? (texts.data?.items.find((t) => t.card_id === preview.id)?.body ?? '') : ''

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Карта ${position}: выберите карту`}
      description="Подставится описание карты для «Трёх карт», его можно поправить перед публикацией."
      footer={
        <>
          <button type="button" className="secondary" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            disabled={!preview || pending || preview.id === current || !available(preview)}
            onClick={() => preview && onPick(preview.id)}
          >
            {pending ? 'Выбираю…' : preview ? `Выбрать «${preview.name}»` : 'Выберите карту'}
          </button>
        </>
      }
    >
      <div className={styles.pickerBar}>
        <div className={styles.seg} role="group" aria-label="Фильтр карт">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <input type="search" className={styles.search} value={query} placeholder="Найти карту по названию" aria-label="Поиск карты" onChange={(e) => setQuery(e.target.value)} />
      </div>
      {error && <p className="error-box">{error}</p>}
      <div className={styles.pickerBody}>
        <div className={styles.tiles} role="listbox" aria-label="Карты колоды для 3 карт">
          {shown.map((c) => {
            const face = faceOf(c)
            const inSet = c.id !== current && taken.includes(c.id)
            return (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={c.id === chosen}
                disabled={!available(c)}
                title={inSet ? 'Уже в наборе' : available(c) ? undefined : 'Нет картинки в колоде'}
                className={styles.tile}
                onClick={() => setChosen(c.id)}
                onDoubleClick={() => c.id !== current && onPick(c.id)}
              >
                <span className={styles.tileImg}>
                  {face ? <img src={face} alt="" loading="lazy" draggable={false} /> : c.arcana === 'major' ? toRoman(c.number) : c.name.charAt(0)}
                  {inSet && <span className={styles.own}>в наборе</span>}
                </span>
                <span className={styles.tileName}>{c.name}</span>
              </button>
            )
          })}
          {shown.length === 0 && <p className={styles.hint}>Ничего не нашлось.</p>}
        </div>
        {preview && (
          <aside className={styles.preview} aria-live="polite">
            <CardFace src={faceOf(preview)} number={preview.arcana === 'major' ? preview.number : null} name={preview.name} size="md" />
            <b>{preview.name}</b>
            <p className={styles.previewBody}>{text || 'Описания для «Трёх карт» нет, текст напишете сами.'}</p>
          </aside>
        )}
      </div>
    </Modal>
  )
}
